import { createFileRoute, Link } from "@tanstack/react-router";
import { PageShell, Card } from "@/components/page-shell";
import { FolkRemedies, RemovalStepList } from "@/components/bite/removal-steps";
import { PrintButton } from "@/components/print-button";
import { SourceList } from "@/components/source-list";
import { absUrl } from "@/lib/site";
import { breadcrumbJsonLd, howToJsonLd } from "@/lib/jsonld";
import { removalHowToSteps } from "@/lib/removal";
import { ONSET, longestWindowDays } from "@/lib/onset";
import { pageTitle } from "@/lib/seo";

export const Route = createFileRoute("/tick-bite-guide")({
  head: () => ({
    meta: [
      { title: pageTitle("What to Do After a Tick Bite: Printable Action Plan") },
      {
        name: "description",
        content:
          "A printable tick bite action plan: how to remove a tick safely, what not to do, and how long to watch for symptoms. Educational, not medical advice.",
      },
      { property: "og:title", content: "Tick Bite Action Plan" },
      {
        property: "og:description",
        content: "A one-page tick bite action plan you can print or keep on your phone.",
      },
      { property: "og:url", content: absUrl("/tick-bite-guide") },
    ],
    links: [{ rel: "canonical", href: absUrl("/tick-bite-guide") }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify(
          howToJsonLd({
            name: "What to do after a tick bite",
            description:
              "A step-by-step action plan for after a tick bite: safe removal, cleaning, and how long to watch for symptoms.",
            path: "/tick-bite-guide",
            steps: removalHowToSteps(),
          }),
        ),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify(
          breadcrumbJsonLd([
            { name: "Home", path: "/" },
            { name: "Tick bite action plan", path: "/tick-bite-guide" },
          ]),
        ),
      },
    ],
  }),
  component: TickBitePage,
});

/** The last day any published window on this site stays open. */
const WATCH_DAYS = longestWindowDays();
// Mid-sentence, a disease named for a thing ("alpha-gal syndrome", "tularemia") is
// lowercase; one named for a place or person ("Heartland virus", "Lyme disease") is not.
const COMMON_NOUN = /^(Ehrlichiosis|Tularemia|Anaplasmosis|Babesiosis|Alpha-gal syndrome)$/;
const inSentence = (name: string, first: boolean) =>
  !first && COMMON_NOUN.test(name) ? name.charAt(0).toLowerCase() + name.slice(1) : name;
const NO_WINDOW = ONSET.filter((e) => !e.window).map((e, i) => inSentence(e.shortName, i === 0));

/** ["a", "b", "c"] -> "a, b and c". */
function listJoin(items: readonly string[]): string {
  return items.length < 2
    ? items.join("")
    : `${items.slice(0, -1).join(", ")} and ${items[items.length - 1]}`;
}

function TickBitePage() {
  return (
    <PageShell
      crumbs={[
        { name: "Home", path: "/" },
        { name: "Tick bite action plan", path: "/tick-bite-guide" },
      ]}
      title="What to do after a tick bite"
      intro="One page you can print or keep on your phone. Most tick bites do not lead to illness, but doing this quickly and writing it down makes everything afterwards easier."
    >
      {/* One wrapper so the gaps can close up on paper: this is the page people print. */}
      <div className="space-y-12 print:space-y-6">
        <div className="space-y-10 print:space-y-4">
          <p className="print-only text-sm">
            Tick bite action plan · LoneStarMap.com · Educational only, not medical advice.
          </p>

          <div className="no-print flex flex-wrap items-center gap-x-6 gap-y-3">
            <PrintButton page="tick-bite-guide" label="Print this plan" variant="primary" />
            <Link to="/tick-bite-tool" className="link text-sm font-semibold">
              Do it step by step instead
            </Link>
          </div>

          <section
            aria-labelledby="emergency-care"
            className="break-avoid max-w-3xl border border-l-[3px] border-border border-l-destructive bg-card px-5 py-4 sm:px-6 sm:py-5 print:py-3"
          >
            <h2 id="emergency-care" className="label-caps text-destructive">
              Emergency first
            </h2>
            <p className="mt-1.5 text-[0.9375rem] leading-relaxed text-foreground">
              Severe allergic reactions, trouble breathing, or throat tightness require{" "}
              <strong className="font-semibold">emergency care</strong>. Call your local emergency
              number.
            </p>
          </section>
        </div>

        <Card className="print:pt-3">
          <h2 className="font-display text-[1.75rem] font-medium leading-tight text-foreground print:text-2xl">
            Removing the tick
          </h2>
          <RemovalStepList className="mt-5 max-w-3xl print:mt-3" />
          <div className="mt-8 max-w-3xl break-inside-avoid print:mt-4">
            <FolkRemedies headingLevel="h3" />
          </div>
        </Card>

        <Card className="break-inside-avoid print:pt-3">
          <h2 className="font-display text-[1.75rem] font-medium leading-tight text-foreground print:text-2xl">
            Then write down the date
          </h2>
          <p className="measure mt-3 text-[0.9375rem] leading-relaxed text-muted-foreground">
            The date you found it is the one detail everything afterwards depends on. Note it, along
            with where on your body it was and where you had been.
          </p>
          <p className="measure mt-3 text-[0.9375rem] leading-relaxed text-foreground">
            Published symptom windows on this site run up to{" "}
            <strong className="font-semibold">{WATCH_DAYS} days</strong> after a bite.{" "}
            {NO_WINDOW.length > 0 && `${listJoin(NO_WINDOW)} have no firm published window at all.`}{" "}
            A window closing is not an all-clear. Most bites cause no illness, and if you feel
            unwell at any point, say that you were bitten and when.
          </p>
          <Link
            to="/tick-bite-tool"
            className="link no-print mt-4 inline-block text-sm font-semibold"
          >
            Log the bite and get a timeline counted from that date
          </Link>
        </Card>

        <Card className="break-inside-avoid print:pt-3">
          <h2 className="font-display text-[1.75rem] font-medium leading-tight text-foreground print:text-2xl">
            Call a doctor if
          </h2>
          <ul className="measure mt-3 space-y-2 text-[0.9375rem] leading-relaxed text-foreground">
            <li>
              <strong className="font-semibold">You get a rash or a fever</strong> in the weeks
              after the bite. Tell them you were bitten, when, and where you had been.
            </li>
            <li>
              <strong className="font-semibold">
                You get hives, swelling or stomach pain a few hours after eating red meat.
              </strong>{" "}
              That can be{" "}
              <Link to="/alpha-gal" className="link">
                alpha-gal syndrome
              </Link>
              .
            </li>
            <li>
              <strong className="font-semibold">You have trouble breathing</strong> or your throat
              feels tight: call your emergency number now.
            </li>
          </ul>
        </Card>
        <SourceList ids={["cdc-remove-tick", "cdc-tickborne", "cdc-ags", "cdc-prevent-bites"]} />

        <Card className="no-print">
          <h2 className="font-display text-xl font-medium text-foreground">Going outside again?</h2>
          <p className="mt-1.5 text-[0.9375rem] leading-relaxed text-muted-foreground">
            Repellents, clothing, and the tick check that catches nymphs.
          </p>
          <Link to="/prevention" className="link mt-2 inline-block text-sm font-semibold">
            The prevention guide
          </Link>
        </Card>

        <p className="text-sm leading-relaxed text-muted-foreground">
          This page is educational only and is not medical advice. See the{" "}
          <Link to="/disclaimer" className="link">
            disclaimer
          </Link>
          .
        </p>
      </div>
    </PageShell>
  );
}
